import type {
  BackendHeaders,
  BackendPathParams,
  BackendQueryParams,
} from '../types';
import type { ApiHttpError } from '../errors';

// Shared building blocks for HTTP-style transports (`http`, `api-stream`,
// and any future variant). The two invokers diverge only in how they
// consume the response body — request construction (URL, headers, body
// serialization) is identical.

export interface NormalizeApiErrorProps {
  payload: unknown;
  statusCode: number;
  fallbackMessage: string;
}

export type NormalizeApiError = (props: NormalizeApiErrorProps) => ApiHttpError;

export const safeJsonParse = async (response: Response): Promise<unknown> => {
  // HTTP 204 "No Content" has no body to parse. Returning `undefined` (rather
  // than letting `response.json()` throw a `SyntaxError` and fall through to
  // the `null` branch) lets the wire match `Promise<void>` consumers cleanly:
  // a void-returning mutation that responds with 204 now resolves to
  // `undefined` rather than `null`, so `Result = void` types in the
  // cloudFunctions namespaces aren't a runtime lie.
  if (response.status === 204) {
    return undefined;
  }

  try {
    return await response.json();
  } catch {
    return null;
  }
};

const buildRoutePath = (path: string, params?: BackendPathParams): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return (
    normalizedPath
      // Match an optional path segment written as `/:paramName?`.
      // Pattern breakdown:
      // - `/` requires the segment boundary so we only remove full path segments.
      // - `:([A-Za-z0-9_]+)` captures the param name after the colon.
      // - `\?` matches the literal `?` suffix that marks the segment optional.
      // We handle optional segments first so a missing `/:firmId?` disappears
      // entirely instead of being treated as a required `:firmId` param below.
      .replace(/\/:([A-Za-z0-9_]+)\?/g, (_match, key: string) => {
        const value = params?.[key];

        if (value === undefined || value === null || value === '') {
          return '';
        }

        return `/${encodeURIComponent(String(value))}`;
      })
      // Match a required path param written as either `:paramName` at the start
      // of the path or `/:paramName` after a slash.
      // Pattern breakdown:
      // - `(^|/)` captures the segment prefix so we preserve either the path
      //   start or the separating slash when we substitute the encoded value.
      // - `:([A-Za-z0-9_]+)` captures the required param name.
      // This runs after the optional-segment pass so `/:firmId?` no longer
      // exists in the string and cannot be mistaken for a required param.
      .replace(
        /(^|\/):([A-Za-z0-9_]+)/g,
        (_match, prefix: string, key: string) => {
          const value = params?.[key];

          if (value === undefined || value === null) {
            throw new Error(
              `Missing required path param "${key}" for route "${normalizedPath}"`,
            );
          }

          return `${prefix}${encodeURIComponent(String(value))}`;
        },
      )
  );
};

const appendQueryValue = (
  searchParams: URLSearchParams,
  key: string,
  value: BackendQueryParams[string],
): void => {
  if (Array.isArray(value)) {
    value.forEach((entry) => {
      appendQueryValue(searchParams, key, entry);
    });

    return;
  }

  if (value === undefined || value === null) {
    return;
  }

  searchParams.append(key, String(value));
};

export const buildRouteUrl = (
  apiBaseUrl: string,
  path: string,
  params?: BackendPathParams,
  query?: BackendQueryParams,
): string => {
  const routePath = buildRoutePath(path, params);
  const searchParams = new URLSearchParams();

  Object.entries(query ?? {}).forEach(([key, value]) => {
    appendQueryValue(searchParams, key, value);
  });

  const queryString = searchParams.toString();
  return `${apiBaseUrl}${routePath}${queryString ? `?${queryString}` : ''}`;
};

interface BuildRequestHeadersProps {
  getIdToken: () => Promise<string | undefined>;
  customHeaders?: BackendHeaders;
  includeJsonContentType?: boolean;
  // Transport-specific defaults (e.g. `accept: text/event-stream` for SSE).
  // Spread before `customHeaders` so a caller-supplied override still wins.
  defaultHeaders?: Record<string, string>;
}

export const buildRequestHeaders = async ({
  getIdToken,
  customHeaders,
  includeJsonContentType = true,
  defaultHeaders,
}: BuildRequestHeadersProps): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {
    ...(includeJsonContentType ? { 'content-type': 'application/json' } : {}),
    ...(defaultHeaders ?? {}),
    ...(customHeaders ?? {}),
  };

  const idToken = await getIdToken();
  if (idToken) {
    headers.authorization = `Bearer ${idToken}`;
  }

  return headers;
};
